import { useState } from 'preact/hooks';
import { useFieldArray } from 'react-hook-form';
/**
 * Manages benefits list state, change detection, drag-and-drop reorder,
 * save/cancel handlers, and dirty-empty validation.
 *
 * Used by MeetingPanel, DemoPanel, and DownloadPanel.
 */
export function useBenefitsList({ form, serverBenefits, editing, setEditing, onSave, }) {
    const [saving, setSaving] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'benefits' });
    const { register, getFieldState, formState } = form;
    const watchedBenefits = form.watch('benefits') || [];
    const hasDirtyEmpty = watchedBenefits.some((x, idx) => {
        const val = (x?.value ?? '').trim();
        const fs = getFieldState(`benefits.${idx}.value`, formState);
        return fs.isDirty && val.length === 0;
    });
    const normInitial = (serverBenefits || []).map(s => s.trim()).filter(Boolean);
    const normCurrent = watchedBenefits.map((x) => (x?.value || '').trim()).filter(Boolean);
    const benefitsChanged = JSON.stringify(normInitial) !== JSON.stringify(normCurrent);
    const save = async () => {
        setSaving(true);
        const clean = (form.getValues('benefits') || [])
            .map((b) => (b?.value || '').trim())
            .filter(Boolean);
        const success = await onSave(clean);
        if (success)
            setEditing(false);
        setSaving(false);
    };
    const cancel = () => {
        setEditing(false);
        const next = (serverBenefits || []).map(b => ({ value: b }));
        form.reset({ ...form.getValues(), benefits: next });
    };
    return {
        editing, setEditing,
        saving,
        fields: fields,
        append, remove, move,
        watchedBenefits,
        hasDirtyEmpty,
        benefitsChanged,
        normInitial,
        save, cancel,
        dragIndex, dragOverIndex, setDragIndex, setDragOverIndex,
        register, getFieldState, formState,
    };
}
